import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralEpics = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projId || "");
  const [epics, setEpics] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingEpic, setEditingEpic] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProjectId]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    name: "",
    description: "",
    priority: "Medium",
    status: "Planned",
  };

  const [formData, setFormData] = useState(initialForm);

  // -----------------------
  // Sample Data
  // -----------------------

  const demoProjects = [
    { id: 1, name: "Website Redesign 2026" },
    { id: 2, name: "ERP Integration Phase 1" },
    { id: 3, name: "Customer Support Portal" },
  ];

  const demoEpics = [
    {
      id: 1,
      projectId: 1,
      name: "User Authentication",
      description: "Login, signup and password management",
      priority: "High",
      status: "Active",
    },
    {
      id: 2,
      projectId: 1,
      name: "Homepage Experience",
      description: "Hero section and navigation redesign",
      priority: "Medium",
      status: "Planned",
    },
    {
      id: 3,
      projectId: 2,
      name: "Inventory Sync",
      description: "ERP inventory data synchronization",
      priority: "High",
      status: "Active",
    },
  ];

  const loadSample = () => {
    setProjects(demoProjects);
    setEpics(demoEpics);
  };

  // -----------------------
  // CRUD
  // -----------------------

  const handleAddNew = () => {
    if (!selectedProjectId) {
      alert("Please select a project first.");
      return;
    }
    setEditingEpic(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (epic) => {

    setEditingEpic(epic);

    setFormData({
      name: epic.name,
      description: epic.description,
      priority: epic.priority,
      status: epic.status,
    });

    setShowModal(true);
  };

  const handleDelete = (id) => {

    if (!window.confirm("Delete this epic?")) return;

    setEpics((prev) => prev.filter((e) => e.id !== id));
  };

  const handleSubmit = (e) => {

    e.preventDefault();

    const newEpic = {
      id: editingEpic?.id || Date.now(),
      projectId: Number(selectedProjectId),
      name: formData.name,
      description: formData.description,
      priority: formData.priority,
      status: formData.status,
    };

    if (editingEpic) {

      setEpics((prev) =>
        prev.map((e) => (e.id === editingEpic.id ? newEpic : e))
      );

    } else {

      setEpics((prev) => [...prev, newEpic]);

    }

    setShowModal(false);
    setFormData(initialForm);

  };

  // -----------------------
  // Filtering
  // -----------------------

  const filteredProjectEpics = epics.filter(
    (e) => String(e.projectId) === String(selectedProjectId)
  );

  const filtered = filteredProjectEpics.filter(
    (e) =>
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      (e.description || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const statusColor = {
    Planned: "bg-indigo-600",
    Active: "bg-emerald-600",
    Completed: "bg-gray-600",
    "On Hold": "bg-amber-600",
  };

  const priorityColor = {
    High: "text-red-400",
    Medium: "text-yellow-400",
    Low: "text-green-400",
  };

  return (

    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">

      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            🗂 SprintCentral Epics
          </div>
          <div className="text-xs text-gray-400">
            Manage Epics & Large Feature Initiatives
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      </header>

      {/* MAIN */}

      <div className="mt-4 min-h-[75vh]">

        <div className="mt-4 flex flex-col lg:flex-row gap-4">

          <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> Sample Epics
          </button>

          <button
            onClick={handleAddNew}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Epic
          </button>

          {!projId && (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg h-12 px-3 py-2"
            >
              <option value="">Select Project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
              type="text"
              placeholder="Search epics..."
              value={search}
              style={{ width: "100%" }}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />
          </div>

        </div>
        {showModal && (
          <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-2">

            <div className="flex justify-between items-center mb-4">

              <h2 className="text-lg text-cyan-200 font-semibold">
                {editingEpic ? "Edit Epic" : "New Epic"}
              </h2>

              <button
                className="w-12 bg-blue-500"
                onClick={() => setShowModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>

            </div>

            <form onSubmit={handleSubmit} className="space-y-3">

              <input
                type="text"
                placeholder="Epic Name"
                value={formData.name}
                required
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <textarea
                placeholder="Epic Description"
                style={{ height: "100px", width: "100%" }}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <div className="flex gap-3">

                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  className="w-40 h-12 bg-gray-900 border border-gray-700 rounded p-2"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>

                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className="w-40 h-12 bg-gray-900 border border-gray-700 rounded p-2"
                >
                  <option value="Planned">Planned</option>
                  <option value="Active">Active</option>
                  <option value="Completed">Completed</option>
                  <option value="On Hold">On Hold</option>
                </select>

              </div>

              <button
                type="submit"
                className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
              >
                {editingEpic ? "Update Epic" : "Create Epic"}
              </button>

            </form>

          </div>
        )}        

        {/* EPIC CARDS */}

        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-4">

          {paginated.map((epic) => (

            <div
              key={epic.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5"
            >

              <div className="flex justify-between items-start">

                <h3 className="font-semibold text-white">{epic.name}</h3>

                <span
                  className={`px-2 py-1 rounded text-xs ${statusColor[epic.status]}`}
                >
                  {epic.status}
                </span>

              </div>

              <p className="text-sm text-gray-300 mt-2">
                {epic.description || "No epic description"}
              </p>

              <div className={`text-xs mt-3 ${priorityColor[epic.priority]}`}>
                Priority: {epic.priority}
              </div>

              <div className="mt-4 flex gap-2">

                <button
                  onClick={() => handleEdit(epic)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>

                <button
                  onClick={() => handleDelete(epic.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>

              </div>

            </div>

          ))}

        </div>

      </div>

      {/* FOOTER */}

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>

    </div>

  );

};

export default SprintCentralEpics;